import type { ChangeEvent } from 'react';
import { Button, Input, Select } from 'antd';
import { Search } from 'lucide-react';

import { ADMIN_RESULTS_SEARCH_MAX_LENGTH, type AdminHtsSubmissionFilterStatus, type HtsReadinessBand } from '@evaltick/shared';

import { ResultsDateRangeField } from './ResultsDateRangeField';
import type { ResultsFilterOption } from './resultsFilterOptions';
import styles from './HtsSubmissionsFilterBar.module.scss';

interface HtsSubmissionsFilterBarProps {
  searchValue: string;
  onSearchChange: (e: ChangeEvent<HTMLInputElement>) => void;
  statusOptions: ResultsFilterOption<AdminHtsSubmissionFilterStatus>[];
  status?: AdminHtsSubmissionFilterStatus;
  onStatusChange: (status?: AdminHtsSubmissionFilterStatus) => void;
  /** Feeds both the Score and Readiness controls below — same underlying value, shown once per matching column. */
  bandOptions: ResultsFilterOption<HtsReadinessBand>[];
  band?: HtsReadinessBand;
  onBandChange: (band?: HtsReadinessBand) => void;
  /** `YYYY-MM-DD` bounds — see DATE_PARAM_FORMAT. */
  from?: string;
  to?: string;
  onDateRangeChange: (from?: string, to?: string) => void;
  hasActiveFilters: boolean;
  onClear: () => void;
}

/**
 * The search/filter row above the admin HTS Submissions list. Ordered to
 * match the table's own column order (User, Status, Score, Readiness, Date).
 * Score and Readiness are the same underlying readiness band, shown as two
 * controls so each sits under its matching column — selecting one updates
 * both.
 */
export function HtsSubmissionsFilterBar({
  searchValue,
  onSearchChange,
  statusOptions,
  status,
  onStatusChange,
  bandOptions,
  band,
  onBandChange,
  from,
  to,
  onDateRangeChange,
  hasActiveFilters,
  onClear,
}: HtsSubmissionsFilterBarProps) {
  return (
    <div className={styles.filterBar} role="search" aria-label="Filter HTS submissions">
      <Input
        placeholder="Search name, email, tag or package"
        aria-label="Search HTS submissions"
        value={searchValue}
        onChange={onSearchChange}
        maxLength={ADMIN_RESULTS_SEARCH_MAX_LENGTH}
        allowClear
        prefix={<Search className={styles.inputIcon} />}
        className={styles.search}
      />
      <Select<AdminHtsSubmissionFilterStatus>
        placeholder="Status"
        aria-label="Filter HTS submissions by status"
        value={status}
        onChange={(value) => onStatusChange(value || undefined)}
        options={statusOptions}
        allowClear
        className={styles.status}
      />
      <Select<HtsReadinessBand>
        placeholder="Score"
        aria-label="Filter HTS submissions by score"
        value={band}
        onChange={(value) => onBandChange(value || undefined)}
        options={bandOptions}
        allowClear
        className={styles.score}
      />
      <Select<HtsReadinessBand>
        placeholder="Readiness"
        aria-label="Filter HTS submissions by readiness"
        value={band}
        onChange={(value) => onBandChange(value || undefined)}
        options={bandOptions}
        allowClear
        className={styles.readiness}
      />
      <ResultsDateRangeField
        ariaLabel="Filter HTS submissions by date"
        from={from}
        to={to}
        onChange={onDateRangeChange}
        className={styles.dates}
      />
      <Button type="link" onClick={onClear} disabled={!hasActiveFilters} className={styles.clear}>
        Clear filters
      </Button>
    </div>
  );
}
